import { useEffect, useRef } from 'react';

/** Delay past a boundary before refetching, so the server has crossed it too. */
export const CHECK_IN_BOUNDARY_BUFFER_MS = 1000;

/**
 * Delay before the one follow-up refetch when a boundary refetch left the
 * check-in state unchanged (device clock running ahead of the server's).
 */
export const CHECK_IN_BOUNDARY_RETRY_MS = 5000;

/** Longest delay `setTimeout` honours (2^31 − 1 ms, ~24.8 days); longer ones fire immediately. */
export const MAX_TIMEOUT_MS = 2 ** 31 - 1;

interface CheckInBoundaries {
  opensAt?: string | null;
  closesAt?: string | null;
}

/**
 * Epoch ms at which to react to the nearest scheduled check-in transition
 * (boundary + buffer) that is still ahead of `now`; null when none is.
 */
export function getNextCheckInBoundary({
  opensAt,
  closesAt,
  now = Date.now(),
}: CheckInBoundaries & { now?: number }): number | null {
  const upcoming = [opensAt, closesAt]
    .filter((iso): iso is string => Boolean(iso))
    .map(iso => new Date(iso).getTime() + CHECK_IN_BOUNDARY_BUFFER_MS)
    .filter(at => Number.isFinite(at) && at > now);
  return upcoming.length ? Math.min(...upcoming) : null;
}

/**
 * Arms a single timer for the nearest boundary, then re-arms for the next one.
 * Delays beyond `MAX_TIMEOUT_MS` are waited out in chunks. `CHECK_IN_BOUNDARY_RETRY_MS`
 * after a boundary's `onBoundary` settles, if `getIsOpen()` still reads what it
 * read before the boundary, `onBoundary` runs once more (never more than once
 * per boundary). Returns the cancel function.
 */
export function scheduleCheckInBoundary({
  opensAt,
  closesAt,
  onBoundary,
  getIsOpen,
}: CheckInBoundaries & {
  onBoundary: () => unknown;
  getIsOpen: () => boolean;
}): () => void {
  let boundaryHandle: ReturnType<typeof setTimeout> | undefined;
  const retryHandles = new Set<ReturnType<typeof setTimeout>>();
  let cancelled = false;

  const retryIfUnchanged = (wasOpen: boolean) => {
    const handle = setTimeout(() => {
      retryHandles.delete(handle);
      if (getIsOpen() === wasOpen) onBoundary();
    }, CHECK_IN_BOUNDARY_RETRY_MS);
    retryHandles.add(handle);
  };

  const fireBoundary = async () => {
    const wasOpen = getIsOpen();
    arm();
    try {
      await onBoundary();
    } finally {
      if (!cancelled) retryIfUnchanged(wasOpen);
    }
  };

  const arm = () => {
    const boundary = getNextCheckInBoundary({ opensAt, closesAt });
    if (boundary === null) return;
    const delay = boundary - Date.now();
    if (delay > MAX_TIMEOUT_MS) {
      boundaryHandle = setTimeout(arm, MAX_TIMEOUT_MS);
      return;
    }
    boundaryHandle = setTimeout(fireBoundary, delay);
  };

  arm();
  return () => {
    cancelled = true;
    if (boundaryHandle !== undefined) clearTimeout(boundaryHandle);
    retryHandles.forEach(clearTimeout);
  };
}

/**
 * Calls `onBoundary` (typically: invalidate the event detail + session queries)
 * just after the event's scheduled check-in opens or closes, so a screen left
 * open flips state without a reload. `isOpen` is the check-in state the screen
 * resolves; if a boundary refetch leaves it unchanged, one follow-up refetch
 * runs a few seconds later. Re-armed whenever the boundaries change; cleared on
 * unmount.
 */
export function useCheckInBoundaryRefetch({
  opensAt,
  closesAt,
  isOpen,
  onBoundary,
}: CheckInBoundaries & { isOpen: boolean; onBoundary: () => unknown }): void {
  const onBoundaryRef = useRef(onBoundary);
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    onBoundaryRef.current = onBoundary;
    isOpenRef.current = isOpen;
  });

  useEffect(
    () =>
      scheduleCheckInBoundary({
        opensAt,
        closesAt,
        onBoundary: () => onBoundaryRef.current(),
        getIsOpen: () => isOpenRef.current,
      }),
    [opensAt, closesAt],
  );
}
